import React from 'react';
import { createPortal } from 'react-dom';
import type { PointTypeItem, PointTypeSet } from '../../types/pointItemNote';
import PointTypeIcon from './PointTypeIcon';
import GemStarIcon from './GemStarIcon';

export type PointTypeSelectResult =
  | { kind: 'preset'; type: PointTypeItem }
  | { kind: 'manual'; category: 'positive' | 'negative' };

type Props = {
  open: boolean;
  pointSet: PointTypeSet;
  onClose: () => void;
  onSelect: (result: PointTypeSelectResult) => void;
  onEditSets: () => void;
  onRefresh?: () => void;
};

const PointTypeSelectModal: React.FC<Props> = ({
  open,
  pointSet,
  onClose,
  onSelect,
  onEditSets,
  onRefresh,
}) => {
  if (!open) return null;

  return createPortal(
    <div className="cp-pin-pts-overlay" role="presentation" onClick={onClose}>
      <div
        className="cp-pin-pts-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-pin-pts-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cp-pin-pts-header">
          <h3 id="cp-pin-pts-title">포인트 타입 및 세트 선택</h3>
          <div className="cp-pin-pts-header-actions">
            {onRefresh ? (
              <button
                type="button"
                className="cp-pin-pts-icon-btn"
                title="새로고침"
                aria-label="새로고침"
                onClick={(e) => {
                  e.stopPropagation();
                  onRefresh();
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 3v5h-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3 21v-5h5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ) : null}
            <button
              type="button"
              className="cp-pin-pts-icon-btn"
              title="포인트 타입별 세트 설정"
              aria-label="포인트 타입별 세트 설정"
              onClick={(e) => {
                e.stopPropagation();
                onEditSets();
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className="cp-pin-pts-icon-btn cp-pin-pts-close"
              onClick={onClose}
              aria-label="닫기"
            >
              ×
            </button>
          </div>
        </header>

        <div className="cp-pin-pts-body">
          <section className="cp-pin-pts-section">
            <h4 className="cp-pin-pts-section-title">긍정행동 포인트</h4>
            <button
              type="button"
              className="cp-pin-pts-item"
              onClick={() => onSelect({ kind: 'manual', category: 'positive' })}
            >
              <GemStarIcon positive size={20} />
              <span className="cp-pin-pts-item-info">
                <span className="cp-pin-pts-memo is-muted">메모 직접 입력</span>
                <span className="cp-pin-pts-points is-muted">포인트 직접 입력</span>
              </span>
            </button>
            {pointSet.positiveTypes.map((type) => (
              <button
                key={type.id}
                type="button"
                className="cp-pin-pts-item"
                onClick={() => onSelect({ kind: 'preset', type })}
              >
                <PointTypeIcon positive emoji={type.emoji} size={20} />
                <span className="cp-pin-pts-item-info">
                  <span className="cp-pin-pts-memo">{type.memo || '이름 없음'}</span>
                  <span className="cp-pin-pts-points">{type.points}점</span>
                </span>
              </button>
            ))}
          </section>

          <section className="cp-pin-pts-section">
            <h4 className="cp-pin-pts-section-title">행동체크 포인트</h4>
            <button
              type="button"
              className="cp-pin-pts-item"
              onClick={() => onSelect({ kind: 'manual', category: 'negative' })}
            >
              <GemStarIcon positive={false} size={20} />
              <span className="cp-pin-pts-item-info">
                <span className="cp-pin-pts-memo is-muted">메모 직접 입력</span>
                <span className="cp-pin-pts-points is-muted">포인트 직접 입력</span>
              </span>
            </button>
            {pointSet.negativeTypes.map((type) => (
              <button
                key={type.id}
                type="button"
                className="cp-pin-pts-item"
                onClick={() => onSelect({ kind: 'preset', type })}
              >
                <PointTypeIcon positive={false} emoji={type.emoji} size={20} />
                <span className="cp-pin-pts-item-info">
                  <span className="cp-pin-pts-memo">{type.memo || '이름 없음'}</span>
                  <span className="cp-pin-pts-points">{type.points}점</span>
                </span>
              </button>
            ))}
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PointTypeSelectModal;
